import { isPublicOrderId } from '../../shared/publicOrderId.ts'
import type { CartBox } from '../order/cartStorage'

export const CONFIRMATION_SNAPSHOT_PREFIX = 'sg.checkout.confirmation.v1:'
export const CONFIRMATION_SNAPSHOT_MAX_AGE_MS = 24 * 60 * 60 * 1000

export type ConfirmationMethod = 'card' | 'bank-transfer'

export type ConfirmationAddOn = {
  id: string
  name: string
  packLabel: string
  unitPriceCents: number
  quantity: number
  imageUrl?: string | null
}

export type ConfirmationSnapshot = {
  version: 1
  orderId: string
  method: ConfirmationMethod
  paymentStatus: 'paid' | 'pending'
  amountCents: number
  subtotalCents?: number
  discountCents?: number
  deliveryFeeCents?: number
  purchaseSuppressed?: boolean
  measurementToken?: string
  placedAt: string
  boxes: CartBox[]
  addons?: ConfirmationAddOn[]
  contact: {
    fullName: string
    phone: string
    email: string
  }
  delivery: {
    address: string
    suburb: string
    postcode: string
    deliveryDay: string
    deliveryNotes: string
  }
}

export type ConfirmationState =
  | { kind: 'valid'; snapshot: ConfirmationSnapshot }
  | { kind: 'invalid'; reason: 'missing-or-malformed-context' | 'missing-or-invalid-snapshot' }

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function validOptionalMoney(value: unknown): boolean {
  return value === undefined || (Number.isInteger(value) && Number(value) >= 0)
}

function validConfirmationAddOns(value: unknown): boolean {
  if (value === undefined) return true
  if (!Array.isArray(value)) return false
  const ids = new Set<string>()
  return value.every((raw) => {
    const item = asRecord(raw)
    if (!item
      || !nonEmptyString(item.id)
      || !nonEmptyString(item.name)
      || !nonEmptyString(item.packLabel)
      || !Number.isInteger(item.unitPriceCents) || Number(item.unitPriceCents) < 1
      || !Number.isInteger(item.quantity) || Number(item.quantity) < 1
      || (item.imageUrl !== undefined && item.imageUrl !== null && typeof item.imageUrl !== 'string')) return false
    const id = String(item.id)
    if (ids.has(id)) return false
    ids.add(id)
    return true
  })
}

export function isConfirmationSnapshot(
  value: unknown,
  orderId: string,
  method: ConfirmationMethod,
  nowMs = Date.now(),
): value is ConfirmationSnapshot {
  const record = asRecord(value)
  if (!record) return false

  const contact = asRecord(record.contact)
  const delivery = asRecord(record.delivery)
  const placedAtMs = typeof record.placedAt === 'string' ? Date.parse(record.placedAt) : Number.NaN
  const ageMs = nowMs - placedAtMs
  const expectedPaymentStatus = method === 'card' ? 'paid' : 'pending'

  return record.version === 1
    && record.orderId === orderId
    && isPublicOrderId(orderId)
    && record.method === method
    && record.paymentStatus === expectedPaymentStatus
    && Number.isInteger(record.amountCents)
    && Number(record.amountCents) > 0
    && validOptionalMoney(record.subtotalCents)
    && validOptionalMoney(record.discountCents)
    && validOptionalMoney(record.deliveryFeeCents)
    && (record.purchaseSuppressed === undefined || typeof record.purchaseSuppressed === 'boolean')
    && (record.measurementToken === undefined
      || (typeof record.measurementToken === 'string' && /^(?:cs_|pi_)[A-Za-z0-9_]+$/.test(record.measurementToken)))
    && Number.isFinite(placedAtMs)
    && ageMs >= -5 * 60 * 1000
    && ageMs <= CONFIRMATION_SNAPSHOT_MAX_AGE_MS
    && Array.isArray(record.boxes)
    && record.boxes.length > 0
    && validConfirmationAddOns(record.addons)
    && Boolean(contact)
    && nonEmptyString(contact?.fullName)
    && nonEmptyString(contact?.phone)
    && nonEmptyString(contact?.email)
    && Boolean(delivery)
    && nonEmptyString(delivery?.address)
    && nonEmptyString(delivery?.suburb)
    && nonEmptyString(delivery?.deliveryDay)
}

export function resolveConfirmationState(
  params: URLSearchParams,
  readSnapshot: (orderId: string) => unknown,
  qaSnapshot: ConfirmationSnapshot | null = null,
  nowMs = Date.now(),
): ConfirmationState {
  if (qaSnapshot) {
    return isConfirmationSnapshot(qaSnapshot, qaSnapshot.orderId, qaSnapshot.method, nowMs)
      ? { kind: 'valid', snapshot: qaSnapshot }
      : { kind: 'invalid', reason: 'missing-or-invalid-snapshot' }
  }

  const rawMethod = params.get('method')
  const rawOrderId = params.get('order')?.trim() ?? ''
  if ((rawMethod !== 'card' && rawMethod !== 'bank-transfer') || !isPublicOrderId(rawOrderId)) {
    return { kind: 'invalid', reason: 'missing-or-malformed-context' }
  }

  const candidate = readSnapshot(rawOrderId)
  if (!isConfirmationSnapshot(candidate, rawOrderId, rawMethod, nowMs)) {
    return { kind: 'invalid', reason: 'missing-or-invalid-snapshot' }
  }

  return { kind: 'valid', snapshot: candidate }
}
